import { useMemo, useCallback, useState } from 'react';
import { Copy, Check } from 'lucide-react';
import type { QueryResultSet } from '../../../../types/queryExecution';
import type { QueryMessage } from '../../core/types/workspaceTypes';
import { buildPlainTextOutput, shouldFilterMessage } from '../utils/plaintextResultsUtils';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../../components/ui/tooltip';
import { writeClipboardText } from '../../../../lib/clipboard';
import { useAnalytics } from '@/core/analytics/useAnalytics';

interface PlaintextResultsViewProps {
  resultSets?: QueryResultSet[];
  messages?: QueryMessage[];
  className?: string;
  /** Info note shown when rendering limited data (e.g., windowed mode) */
  infoNote?: string;
}

export function PlaintextResultsView({
  resultSets,
  messages,
  className = '',
  infoNote,
}: PlaintextResultsViewProps) {
  const [copied, setCopied] = useState(false);
  const { track } = useAnalytics();

  const plainText = useMemo(() => {
    return buildPlainTextOutput(resultSets, messages);
  }, [resultSets, messages]);

  const handleCopy = useCallback(async () => {
    const ok = await writeClipboardText(plainText);
    if (!ok) return;
    track('feature:results_copy_plaintext');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [plainText, track]);

  const hasResults = (resultSets && resultSets.length > 0 &&
    resultSets.some(rs => (rs.rows ?? []).length > 0 || (rs.columns ?? []).length > 0)) ||
    (messages && messages.some(m => !shouldFilterMessage(m.content)));

  return (
    <div className={`w-full h-full flex flex-col overflow-hidden bg-background ${className}`} data-testid="plaintext-results-view">
      {/* Toolbar */}
      <div className="flex-shrink-0 flex items-center gap-2 px-3 py-1.5 bg-muted/30 border-b border-border select-none">
        <span className="text-xs text-muted-foreground flex-1">
          Plain text results
        </span>
        {hasResults && (
          <Tooltip open={copied ? true : undefined}>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={handleCopy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md
                           text-muted-foreground hover:text-foreground hover:bg-muted/50
                           transition-colors"
                data-testid="copy-plaintext-button"
              >
                {copied ? (
                  <Check className="h-3.5 w-3.5 text-green-500" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
                Copy
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {copied ? 'Data copied' : 'Copy to clipboard'}
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-auto p-3">
        {infoNote && (
          <div className="mb-2 px-2 py-1.5 text-xs text-muted-foreground bg-muted/40 rounded border border-border">
            {infoNote}
          </div>
        )}
        <pre className="font-mono text-xs text-foreground whitespace-pre leading-relaxed">
          {plainText}
        </pre>
      </div>
    </div>
  );
}
